import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useAppContext } from "./context/AppContext.js";
import { AuthProvider, useAuth } from "./context/AuthContext.js";
import ProtectedRoute from "./components/ProtectedRoute.js";
import UnauthorizedPage from "./pages/UnauthorizedPage.js";
import ManageAccess from "./pages/ManageAccess.jsx";
import Sidebar from "./components/layout/Sidebar.js";
import Topbar from "./components/layout/Topbar.js";
import ScrollToTop from "./components/layout/ScrollToTop.js";
import Dashboard from "./dashboard/Dashboard.js";
import ProductList from "./pages/products/ProductList.js";
import Employees from "./pages/employee/Employees.js";
import AttendanceLog from "./pages/attendance/AttendanceLog.js";
import AttendanceReport from "./pages/attendance/AttendanceReport.js";
import MyAttendance from "./pages/attendance/MyAttendance.js";
import ProfilePage from "./pages/profile/ProfilePage.js";
import SalarySlip from "./pages/salary/SalarySlip.js";
import Salary from "./pages/salary/Salary.js";
import LoadingScreen from "./components/LoadingScreen.js";
import NotificationsPage from "./pages/notifications/NotificationsPage.js";
import AdminPushPanel from "./pages/admin/AdminPushPanel.js";

// Stock Pages
import StockOverview from "./pages/stock/StockOverview.js";
import RawMaterials from "./pages/stock/RawMaterials.js";
import Expenses from "./pages/expenses/Expenses.js";
import ExpenseReport from "./pages/expenses/ExpenseReport.js";
import StockTransfer from "./pages/stock/StockTransfer.js";
import LowStockAlert from "./pages/stock/LowStockAlert.js";

// Production Pages
import ProductionPlan from "./pages/production/ProductionPlan.js";
import DailyProduction from "./pages/production/Daily.js";

// Auth Pages
import Login from "./pages/login/Login.js";
import ForgotPassword from "./pages/forgot-password/ForgotPassword.js";
import ResetPassword from "./pages/forgot-password/ResetPassword.js";

// Report Pages
import StockReport from "./pages/reports/StockReport.js";
import ProductionReportPage from "./pages/reports/ProductionReport.js";
import SalesReport from "./pages/reports/SalesReport.js";
import EmployeeReport from "./pages/reports/EmployeeReport.js";
import FinancialReport from "./pages/reports/FinancialReport.js";
import CheckPage from "./pages/CheckPage.js";

// Client Pages
import Clients from "./pages/clients/Clients.js";

// Vendor Pages
import Vendors from "./pages/vendors/Vendors.js";

// Turnover Pages
import Turnover from "./pages/turnover/Turnover.js";

import Sales from "./pages/sales/Sales.js";
import SalesHistory from "./pages/sales/SalesHistory.js";
import Help from "./pages/Help.js";
import ChangePasswordModal from "./components/ChangePasswordModal.jsx";
import PrivacyPolicy from "./pages/privacy/PrivacyPolicy.js";

// Public Route Component (Redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const fastAuth = !!localStorage.getItem('userInfo');

  if (loading && !fastAuth) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  const { loading: appLoading, isUpdating } = useAppContext();
  const { user } = useAuth();
  const [isDismissed, setIsDismissed] = React.useState(false);
  return (
    <div className="dashboard-wrapper">
      {/* Show LoadingScreen on cold start OR when manually refreshing */}
      {(appLoading || isUpdating) && <LoadingScreen />}
      
      {/* Force password change for new credentials */}
      <ChangePasswordModal 
        isOpen={user?.isFirstLogin && !isDismissed} 
        onClose={() => setIsDismissed(true)} 
      />

      <Sidebar />
      <div className="right-area">
        <Topbar />
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
};

  const MyAttendanceRoute = () => {
    const { isAdmin } = useAuth();

    if (isAdmin) {
      return <Navigate to="/attendance" replace />;
    }

    return (
      <ProtectedRoute>
        <AppLayout><MyAttendance /></AppLayout>
      </ProtectedRoute>
    );
  };

const App = () => {
  useEffect(() => {
    const handleOnline = () => document.body.classList.remove('website-offline');
    const handleOffline = () => document.body.classList.add('website-offline');

    // Set initial state
    if (!navigator.onLine) handleOffline();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <AuthProvider>
      <AppProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Dashboard */}
            <Route path="/dashboard" element={
              <ProtectedRoute module="dashboard">
                <AppLayout><Dashboard /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/salary-slip" element={
              <ProtectedRoute module="dashboard">
                <AppLayout><SalarySlip /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/salary" element={
              <ProtectedRoute module="salary">
                <AppLayout><Salary /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Stock Management */}
            <Route path="/stock" element={
              <ProtectedRoute module="stock">
                <AppLayout><StockOverview /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock/raw-materials" element={
              <ProtectedRoute module="stock">
                <AppLayout><RawMaterials /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/expenses" element={
              <ProtectedRoute module="stock">
                <AppLayout><Expenses /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/expenses/report" element={
              <ProtectedRoute module="stock">
                <AppLayout><ExpenseReport /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock/transfer" element={
              <ProtectedRoute module="stock">
                <AppLayout><StockTransfer /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock/low-stock" element={
              <ProtectedRoute module="stock">
                <AppLayout><LowStockAlert /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Products */}
            <Route path="/products" element={
              <ProtectedRoute module="products">
                <AppLayout><ProductList /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Production */}
            <Route path="/production/plan" element={
              <ProtectedRoute module="production">
                <AppLayout><ProductionPlan /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/daily" element={
              <ProtectedRoute module="production">
                <AppLayout><DailyProduction /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Employees */}
            <Route path="/employees" element={
              <ProtectedRoute module="employees">
                <AppLayout><Employees /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Admin Management */}
            <Route path="/admin/manage-access" element={
              <ProtectedRoute adminOnly={true}>
                <AppLayout><ManageAccess /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/push-notifications" element={
              <ProtectedRoute adminOnly={true}>
                <AppLayout><AdminPushPanel /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Clients */}
            <Route path="/clients" element={
              <ProtectedRoute module="clients">
                <AppLayout><Clients /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Vendors */}
            <Route path="/vendors" element={
              <ProtectedRoute module="vendors">
                <AppLayout><Vendors /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Turnover */}
            <Route path="/turnover" element={
              <ProtectedRoute module="turnover">
                <AppLayout><Turnover /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Sales */}
            <Route path="/sales" element={
              <ProtectedRoute module="sales">
                <AppLayout><Sales /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/sales/history" element={
              <ProtectedRoute module="sales">
                <AppLayout><SalesHistory /></AppLayout>
              </ProtectedRoute>
            } />

            {/* Attendance */}
            <Route path="/attendance" element={
              <ProtectedRoute module="attendance">
                <AppLayout><AttendanceLog /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/attendance-report" element={
              <ProtectedRoute module="attendance">
                <AppLayout><AttendanceReport /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/my-attendance" element={
              <MyAttendanceRoute />
            } />

            {/* Reports */}
            <Route path="/reports/stock" element={<ProtectedRoute module="reports"><AppLayout><StockReport /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/production" element={<ProtectedRoute module="reports"><AppLayout><ProductionReportPage /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/sales" element={<ProtectedRoute module="reports"><AppLayout><SalesReport /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/employees" element={<ProtectedRoute module="reports"><AppLayout><EmployeeReport /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/financial" element={<ProtectedRoute module="reports"><AppLayout><FinancialReport /></AppLayout></ProtectedRoute>} />

            {/* Profile (Self-access usually allowed) */}
            <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
            <Route path="/profile/:id" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
            <Route path="/employees/:id" element={<ProtectedRoute module="employees"><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />

            {/* Check System Status */}
            <Route path="/check" element={<ProtectedRoute><AppLayout><CheckPage /></AppLayout></ProtectedRoute>} />

            {/* Help */}
            <Route path="/help" element={<ProtectedRoute><AppLayout><Help /></AppLayout></ProtectedRoute>} />

            {/* Notifications */}
            <Route path="/notifications" element={<ProtectedRoute><AppLayout><NotificationsPage /></AppLayout></ProtectedRoute>} />

            {/* 404 */}
            <Route path="*" element={<ProtectedRoute><AppLayout><div>404 - Page Not Found</div></AppLayout></ProtectedRoute>} />
          </Routes>
        </Router>
      </AppProvider>
    </AuthProvider>
  );
};

export default App;